import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../assets/new-logo.png';

import LogoutButton from '../../components/LogoutButton';
import BrandNotificationBell from '../../components/BrandNotificationBell';
import CreditsWidget from '../../components/CreditsWidget';
import CreditGateModal from '../../components/CreditGateModal';
import CreditDeductionConfirmModal from '../../components/CreditDeductionConfirmModal';
import SubscriptionExpiryPopup from '../../components/SubscriptionExpiryPopup';
import { useCredits } from '../../hooks/useCredits';
import { usePlatformNotifications } from '../../hooks/usePlatformNotifications';
import ProfileAvatar from '../../components/ProfileAvatar';
import { CREDIT_COSTS } from '../../config/creditCosts';
import { useGmbVisibility } from '../gmb-manager/hooks/useGmbStrategy';

const WEBSITE_BUILDER_MODULE_CODE = 'WEBSITE_BUILDER';

interface MarketingComponent {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  iconBg: string;
  comingSoon: boolean;
  needsWebsite?: boolean;
  needsKeywordsLocked?: boolean;
  needsGmbKeywordsLocked?: boolean;
  isGmbDisabledByBusinessType?: boolean;
}

export default function MarketingIntricacies() {
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem('brand_name') || '');
  const [hasWebsite, setHasWebsite] = useState<boolean>(true);
  const [keywordsLocked, setKeywordsLocked] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [showWebsitePopup, setShowWebsitePopup] = useState<boolean>(false);
  const [showUtilityPopup, setShowUtilityPopup] = useState<boolean>(false);
  const [showGmbUtilityPopup, setShowGmbUtilityPopup] = useState<boolean>(false);
  const [lockedFeatureName, setLockedFeatureName] = useState<string>('');
  const [profileData, setProfileData] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string>(() => (localStorage.getItem('logo_url') || '').replace(/"/g, ''));
  const [websiteProjectState, setWebsiteProjectState] = useState<'Draft' | 'Downloaded' | 'None' | null>(null);
  const [showCreditGateModal, setShowCreditGateModal] = useState<boolean>(false);
  const [showDeductionConfirmModal, setShowDeductionConfirmModal] = useState<boolean>(false);
  const [isDeducting, setIsDeducting] = useState<boolean>(false);
  const { balance, modulePricing, invalidateCredits } = useCredits();
  const { notificationsQuery, unreadCountQuery } = usePlatformNotifications();
  const { data: gmbVisibility, isLoading: isGmbVisibilityLoading } = useGmbVisibility();

  useEffect(() => {
    const handleLogoUpdated = () => {
      setLogoUrl((localStorage.getItem('logo_url') || '').replace(/"/g, ''));
    };
    window.addEventListener('logo_updated', handleLogoUpdated);
    return () => window.removeEventListener('logo_updated', handleLogoUpdated);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) return;

        const [resJson, kwRes] = await Promise.allSettled([
          api.getWebsiteProfile(userId),
          api.getKeywordUniverse(userId),
        ]);

        if (resJson.status === 'fulfilled') {
          const profileData = resJson.value.data?.business_profile || resJson.value.business_profile || resJson.value;
          setProfileData(profileData);
          if (profileData && profileData.business_name) {
            setBrandName(profileData.business_name);
            localStorage.setItem('brand_name', profileData.business_name);
          }
          if (profileData?.logo_url) {
            const cleanedLogo = profileData.logo_url.replace(/"/g, '');
            localStorage.setItem('logo_url', cleanedLogo);
            setLogoUrl(cleanedLogo);
          }
          if (profileData) {
            const websitePresent = !!profileData.website_url;
            setHasWebsite(websitePresent);
            if (profileData.website_url) {
              localStorage.setItem('website_url', profileData.website_url);
            } else {
              localStorage.removeItem('website_url');
            }
          }
        }

        if (kwRes.status === 'fulfilled') {
          const isLocked = kwRes.value.data?.is_locked ?? false;
          setKeywordsLocked(isLocked);
          localStorage.setItem('keywords_locked', String(isLocked));
        }
      } catch (err) {
        console.error("Failed to fetch profile/keywords:", err);
      } finally {
        setIsFetching(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchProjectState = async () => {
      try {
        const res = await api.getWebsiteProjectState();
        setWebsiteProjectState(res.data?.state ?? null);
      } catch (err) {
        console.error("Failed to fetch website builder project state:", err);
        setWebsiteProjectState(null);
      }
    };
    fetchProjectState();
  }, []);

  const websiteBuilderCost = modulePricing.find(p => p.module_code === WEBSITE_BUILDER_MODULE_CODE)?.credit_cost ?? null;

  // UX convenience gate only — the backend independently re-checks balance at the
  // actual build-start action. Fails open (not gated) while state is still loading
  // or unknown, rather than false-locking the card.
  const isCreditGatedForWebsite =
    !isFetching &&
    (websiteProjectState === 'None') &&
    (balance?.total_credits_balance ?? 0) < (websiteBuilderCost ?? 5000);

  const marketingComponents: MarketingComponent[] = [
    {
      id: 'content',
      title: 'Social Media Manager',
      description: 'Streamlines content creation with AI-powered topic generation and optimization',
      features: [
        'AI-powered content generation',
        'Optimization assistant',
        'AI-powered images',
      ],
      icon: 'ri-file-text-line',
      iconBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
      comingSoon: false,
    },
    {
      id: 'seo',
      title: 'SEO Manager',
      description: 'Comprehensive SEO tools to improve your search engine rankings and visibility',
      features: [
        'Keyword discovery & tracking',
        'Site health monitoring',
        'Backlink management',
      ],
      icon: 'ri-search-line',
      iconBg: 'bg-gradient-to-br from-teal-500 to-teal-700',
      comingSoon: false,
      needsWebsite: true,
      needsKeywordsLocked: true,
    },
    {
      id: 'utility',
      title: 'Utility Manager',
      description: 'Access focused marketing utilities for keyword planning',
      features: [
        'Keyword planner',
        'Keyword universe locking',
        'SEO and GMB keyword tabs',
      ],
      icon: 'ri-tools-line',
      iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      comingSoon: false,
    },
    {
      id: 'website',
      title: 'Website Builder',
      description: 'Create and manage high-converting landing pages and websites without any coding',
      features: [
        'AI-Generated Content',
        'Pre-built templates',
        'SEO-friendly structure',
      ],
      icon: 'ri-pages-line',
      iconBg: 'bg-gradient-to-br from-violet-400 to-violet-600',
      comingSoon: false,
    },
    {
      id: 'gmb',
      title: 'GMB Manager',
      description: 'Manage your Google Business Profile visibility, reviews, and local SEO performance',
      features: [
        'Profile optimization & health',
        'Review management & replies',
        'Local ranking & map tracker',
      ],
      icon: 'ri-map-pin-line',
      iconBg: 'bg-gradient-to-br from-sky-400 to-sky-600',
      comingSoon: false,
      needsWebsite: false,
    },
    {
      id: 'image-optimizer',
      title: 'Creative Manager',
      description: 'Produce stunning branded visuals and marketing assets for all your channels',
      features: [
        'Brand kit integration',
        'Multi-format resizing',
        'Visual asset library',
      ],
      icon: 'ri-image-2-line',
      iconBg: 'bg-gradient-to-br from-orange-400 to-orange-600',
      comingSoon: true,
    },
    {
      id: 'funnel',
      title: 'Funnel Manager',
      description: 'Track and optimize your entire customer journey from awareness to conversion',
      features: [
        'Lead capture & nurturing',
        'Conversion rate optimization',
        'Funnel analytics & insights',
      ],
      icon: 'ri-filter-3-line',
      iconBg: 'bg-gradient-to-br from-teal-400 to-teal-600',
      comingSoon: true,
    }
  ].map((component) => {
    if (component.id !== 'gmb') return component;

    // Module not yet rolled out for this build — comingSoon (existing behavior)
    if (!__ENABLE_GMB_MODULE__) {
      return { ...component, comingSoon: true, needsWebsite: undefined };
    }

    // Still resolving backend visibility — fail closed as comingSoon
    // to avoid a flash of an incorrectly-active card.
    if (isGmbVisibilityLoading || !gmbVisibility) {
      return { ...component, comingSoon: true, needsWebsite: undefined };
    }

    const gmbVisibilityStatus = gmbVisibility.status ?? gmbVisibility.visibility;

    // Backend says this business type should never see GMB Manager
    // (physical_presence = 'no' -> SEO_FIRST)
    if (gmbVisibilityStatus === 'HIDDEN') {
      return {
        ...component,
        comingSoon: false,
        needsWebsite: undefined,
        isGmbDisabledByBusinessType: true,
      };
    }

    // Business type qualifies (GMB_FIRST/HYBRID) but local keywords
    // are not yet selected in Keyword Planner — show as a locked
    // "setup required" card, same visual treatment as SEO Manager's
    // needsKeywordsLocked pattern, NOT comingSoon.
    if (gmbVisibilityStatus === 'GREYED') {
      return {
        ...component,
        comingSoon: false,
        needsWebsite: false,
        needsGmbKeywordsLocked: true,
      };
    }

    // ACTIVE — qualifies and has keywords, show fully active
    return { ...component, comingSoon: false };
  });

  const handleCardClick = (componentId: string) => {
    if (componentId === 'content') {
      navigate('/social-media-manager');
    } else if (componentId === 'seo') {
      navigate('/dashboard');
    } else if (componentId === 'utility') {
      navigate('/utility-manager');
    } else if (componentId === 'gmb') {
      navigate('/gmb-manager');
    } else if (componentId === 'website') {
      navigate('/website-builder');
    }
  };

  const handleConfirmDeduction = async () => {
    setIsDeducting(true);
    try {
      const res = await api.chargeWebsiteBuilder();
      if (res.status === 'success') {
        setShowDeductionConfirmModal(false);
        await invalidateCredits();
        notificationsQuery.refetch();
        unreadCountQuery.refetch();
        navigate('/website-builder');
      }
    } catch (err) {
      console.error("Deduction failed:", err);
      alert("Failed to charge credits. Please try again.");
    } finally {
      setIsDeducting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Header Bar */}
      <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img
              src={logo}
              alt="Marketing 4SIGHT Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
          <span className="text-gray-300">|</span>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/agency-dashboard')}
              className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Dashboard
            </button>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            <span className="text-gray-600 font-medium max-w-[200px] truncate" title={brandName}>{brandName}</span>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            <span className="text-gray-900 font-semibold">Marketing Dashboard</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <BrandNotificationBell />

          {/* Credits Widget */}
          <CreditsWidget />

          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer group"
            title="Profile"
          >
            <ProfileAvatar logoUrl={logoUrl} fallbackName={brandName} size="sm" />
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap">
              Profile
            </span>
            <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
          </button>
          <LogoutButton
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all"
          >
            <i className="ri-logout-box-r-line text-[18px] text-slate-600"></i>

            <span className="hidden lg:inline text-sm font-semibold text-slate-700">
              Logout
            </span>
          </LogoutButton>
        </div>
      </header>

      {/* Page Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-8 py-10">
          {/* Page Title Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-4">
              <i className="ri-building-line text-emerald-600 text-sm"></i>
              <span className="text-xs font-semibold text-emerald-700">{brandName}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Marketing Dashboard</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Marketing Dashboard depends on the{' '}
              <span className="font-semibold text-gray-700">7 key components</span> tracked under
              one platform —{' '}
              <span className="font-semibold text-gray-700">Marketing 4SIGHT</span>.
            </p>
          </div>

          {/* Component Cards Grid */}
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12 transition-opacity duration-300 ${isFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
            {marketingComponents.map((component) => {
              const isLockedByWebsite = component.needsWebsite && !hasWebsite;
              const isLockedByUtility = component.needsKeywordsLocked && hasWebsite && !keywordsLocked;
              const isLockedByGmbKeywords = component.needsGmbKeywordsLocked === true;
              const isGmbDisabled = component.isGmbDisabledByBusinessType === true;
              const isCreditGated = component.id === 'website' && isCreditGatedForWebsite;
              const isDimmed = component.comingSoon || isLockedByWebsite || isLockedByUtility || isCreditGated || isLockedByGmbKeywords || isGmbDisabled;
              return (
                <div
                  key={component.id}
                  onClick={() => {
                    // SEO Manager deterministic click behavior
                    if (component.id === 'seo') {
                      if (!hasWebsite) {
                        setLockedFeatureName('SEO Manager');
                        setShowWebsitePopup(true);
                        return;
                      }
                      if (!keywordsLocked) {
                        setShowUtilityPopup(true);
                        return;
                      }
                      navigate('/dashboard');
                      return;
                    }

                    if (isGmbDisabled) return;
                    if (isLockedByGmbKeywords) {
                      setShowGmbUtilityPopup(true);
                      return;
                    }
                    if (isLockedByWebsite) {
                      setLockedFeatureName(component.title);
                      setShowWebsitePopup(true);
                      return;
                    }
                    if (isCreditGated) {
                      setShowCreditGateModal(true);
                      return;
                    }
                    if (component.comingSoon) return;
                    if (component.id === 'website' && websiteProjectState === 'None') {
                      setShowDeductionConfirmModal(true);
                      return;
                    }
                    handleCardClick(component.id);
                  }}
                  className={`bg-white rounded-xl border p-6 transition-all relative group ${component.comingSoon
                    ? 'border-gray-200 shadow-sm'
                    : 'border-gray-200 shadow-sm hover:shadow-md hover:border-teal-200 cursor-pointer'
                    } ${(isLockedByWebsite || isLockedByUtility || isCreditGated || isLockedByGmbKeywords || isGmbDisabled) ? 'opacity-50 grayscale' : ''}`}
                >
                  {/* Coming Soon Badge */}
                  {component.comingSoon && (
                    <span className="absolute top-4 right-4 text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      COMING SOON
                    </span>
                  )}

                  {/* Website Required Locked Badge */}
                  {isLockedByWebsite && (
                    <span className="absolute top-4 right-4 text-[10px] font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded flex items-center gap-1">
                      <i className="ri-lock-2-line"></i> LOCKED
                    </span>
                  )}

                  {/* Utility Setup Required Locked Badge */}
                  {isLockedByUtility && (
                    <span className="absolute top-4 right-4 text-[10px] font-semibold text-teal-700 bg-teal-100 px-2 py-1 rounded flex items-center gap-1">
                      <i className="ri-lock-2-line"></i> SETUP REQUIRED
                    </span>
                  )}

                  {isLockedByGmbKeywords && (
                    <span className="absolute top-4 right-4 text-[10px] font-semibold text-sky-700 bg-sky-100 px-2 py-1 rounded flex items-center gap-1">
                      <i className="ri-lock-2-line"></i> SETUP REQUIRED
                    </span>
                  )}

                  {/* Credit Gate Badge */}
                  {isCreditGated && (
                    <span className="absolute top-4 right-4 text-[10px] font-semibold text-amber-600 bg-amber-100 px-2 py-1 rounded flex items-center gap-1">
                      <i className="ri-lock-2-line"></i> {websiteBuilderCost?.toLocaleString()} CREDITS NEEDED
                    </span>
                  )}

                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${component.iconBg} rounded-lg flex items-center justify-center ${(isLockedByWebsite || isLockedByUtility || isCreditGated || isLockedByGmbKeywords || isGmbDisabled) ? 'opacity-60 grayscale' : ''}`}>
                        <i className={`${component.icon} text-white text-lg`}></i>
                      </div>
                      <h3 className={`text-base font-semibold ${isDimmed ? 'text-gray-400' : 'text-gray-900'}`}>
                        {component.title}
                      </h3>
                    </div>
                    {(!component.comingSoon && !isLockedByWebsite && !isLockedByUtility && !isCreditGated && !isLockedByGmbKeywords && !isGmbDisabled) && (
                      <button className="w-8 h-8 flex items-center justify-center text-gray-300 group-hover:text-teal-600 transition-colors">
                        <i className="ri-arrow-right-line text-lg"></i>
                      </button>
                    )}
                    {isLockedByWebsite && (
                      <button className="w-8 h-8 flex items-center justify-center text-orange-300 group-hover:text-orange-500 transition-colors">
                        <i className="ri-lock-unlock-line text-lg"></i>
                      </button>
                    )}
                    {isLockedByUtility && (
                      <button className="w-8 h-8 flex items-center justify-center text-teal-400 group-hover:text-teal-600 transition-colors">
                        <i className="ri-lock-2-line text-lg"></i>
                      </button>
                    )}
                    {isLockedByGmbKeywords && (
                      <button className="w-8 h-8 flex items-center justify-center text-sky-400 group-hover:text-sky-600 transition-colors">
                        <i className="ri-lock-2-line text-lg"></i>
                      </button>
                    )}
                    {isCreditGated && (
                      <button className="w-8 h-8 flex items-center justify-center text-amber-300 group-hover:text-amber-500 transition-colors">
                        <i className="ri-lock-unlock-line text-lg"></i>
                      </button>
                    )}
                  </div>

                  {/* Description */}
                  <p className={`text-sm mb-4 leading-relaxed ${isDimmed ? 'text-gray-400' : 'text-gray-600'}`}>
                    {component.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2">
                    {component.features.map((feature, index) => (
                      <li
                        key={index}
                        className={`flex items-start gap-2 text-sm ${isDimmed ? 'text-gray-400' : 'text-gray-600'}`}
                      >
                        <span className={`mt-1.5 ${isDimmed ? 'text-gray-300' : 'text-gray-400'}`}>•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>


        </div>

        {/* Footer */}
        <footer className="text-center text-xs text-gray-400 py-6 border-t border-gray-100">
          <p>
            &copy; 2026 Marketing 4SIGHT. All rights reserved.{' '}
          </p>
        </footer>
      </main>

      {/* Website Required Popup (Only shown on click when website is missing) */}
      {showWebsitePopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-xl">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-fill text-3xl text-orange-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Website Required</h3>
            <p className="text-gray-600 text-sm mb-6">
              A working website is needed to access <strong>{lockedFeatureName}</strong>. Build your website in the Website Builder, raise a service request, or provide your website URL in the Profile section.
            </p>
            <div className="flex flex-col gap-2.5">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowWebsitePopup(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowWebsitePopup(false);
                    navigate('/website-builder');
                  }}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer text-sm"
                >
                  Website Builder
                </button>
              </div>
              <button
                onClick={() => {
                  setShowWebsitePopup(false);
                  alert("Service Request Submitted! Our team will contact you shortly to build your website.");
                }}
                className="w-full px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-lg transition-colors cursor-pointer text-sm"
              >
                Raise Service Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEO Manager Locked Popup (Only shown on click when website exists but Keyword Universe is not locked) */}
      {showUtilityPopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-xl border border-teal-100">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-tools-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">SEO Manager Setup Required</h3>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Complete Website Setup and Finalize Your SEO Keyword Universe to Activate SEO Manager.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUtilityPopup(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowUtilityPopup(false);
                  navigate('/utility-manager');
                }}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer text-sm font-semibold"
              >
                Go to Utility Manager
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GMB Manager Locked Popup (business type qualifies but no local keywords selected) */}
      {showGmbUtilityPopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-xl border border-sky-100">
            <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-3xl text-sky-600"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">GMB Manager Setup Required</h3>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Select and finalize your local keywords in the Keyword Planner to activate GMB Manager.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowGmbUtilityPopup(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowGmbUtilityPopup(false);
                  navigate('/utility-manager');
                }}
                className="flex-1 px-4 py-2.5 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-colors cursor-pointer text-sm font-semibold"
              >
                Go to Utility Manager
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Website Builder Credit Gate Popup */}
      <CreditGateModal
        isOpen={showCreditGateModal}
        onClose={() => setShowCreditGateModal(false)}
        currentCredits={balance?.total_credits_balance ?? 0}
        requiredCredits={websiteBuilderCost ?? 0}
        headline={`You need ${websiteBuilderCost?.toLocaleString() ?? '5,000'} credits to build a website`}
        subtext={`Website Builder is credit-based — a full site (minimum 6 pages) costs ${websiteBuilderCost?.toLocaleString() ?? '5,000'} credits, charged upfront. Top up your balance to get started.`}
      />

      <CreditDeductionConfirmModal
        isOpen={showDeductionConfirmModal}
        onClose={() => setShowDeductionConfirmModal(false)}
        onConfirm={handleConfirmDeduction}
        isProcessing={isDeducting}
        deductCredits={websiteBuilderCost ?? CREDIT_COSTS.FULL_WEBSITE ?? 5000}
        currentCredits={balance?.total_credits_balance ?? 0}
      />
    </div>
  );
}
